import { css } from '@adornis/chemistry/directives/css';
import type { Renderable } from '@adornis/chemistry/renderable';
import { html, type TemplateResult } from 'lit';
import '../client/components/fps-fonts';

export enum AICheckerAnswer {
  question_1_1 = 'question_1_1',
  question_1_2 = 'question_1_2',

  question_2_1 = 'question_2_1',
  question_2_2 = 'question_2_2',

  question_3_1 = 'question_3_1',
  question_3_2 = 'question_3_2',

  question_4_1 = 'question_4_1',
  question_4_2 = 'question_4_2',

  QUESTION_5_1 = 'QUESTION_5_1',
  QUESTION_5_2 = 'QUESTION_5_2',
  QUESTION_5_3 = 'QUESTION_5_3',
  QUESTION_5_4 = 'QUESTION_5_4',
  QUESTION_5_5 = 'QUESTION_5_5',
  QUESTION_5_6 = 'QUESTION_5_6',
  QUESTION_5_7 = 'QUESTION_5_7',

  QUESTION_6_1 = 'QUESTION_6_1',
  QUESTION_6_2 = 'QUESTION_6_2',
  QUESTION_6_3 = 'QUESTION_6_3',
  QUESTION_6_4 = 'QUESTION_6_4',
  QUESTION_6_5 = 'QUESTION_6_5',

  QUESTION_8_1 = 'QUESTION_8_1',
  QUESTION_8_2 = 'QUESTION_8_2',

  QUESTION_9_1 = 'QUESTION_9_1',
  QUESTION_9_2 = 'QUESTION_9_2',
}

export type QuestionType = 'single' | 'multiple';

export interface BaseQuestion {
  question: Renderable;
  type: QuestionType;
  choices: AICheckerAnswer[];
  getChoiceText: (choice: AICheckerAnswer) => Renderable;
  getExplanation: () => TemplateResult | null;
  shouldContinue: (choices: AICheckerAnswer[]) => boolean;
  isSkipped: (choices: AICheckerAnswer[]) => boolean;
}

export class Question1 implements BaseQuestion {
  question: string = 'Liegt überhaupt ein KI-System vor?';
  type: QuestionType = 'single';
  choices: AICheckerAnswer[] = [AICheckerAnswer.question_1_1, AICheckerAnswer.question_1_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.question_1_1:
        return 'Ja';
      case AICheckerAnswer.question_1_2:
        return 'Nein';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => html`
    <fps-text>
      KI-Systeme sind Software- und Hardwaresysteme, die z. B. auf Grundlage von Wahrnehmungen und Analysen mit ihrer
      Umgebung agieren, Muster erkennen und autonome Entscheidungen auf Basis von Gelerntem treffen können. <br />
      <br />

      KI-Systeme sind zum Beispiel Chat GPT oder lernende Bilderkennung. Die Funktionalitäten von KI-Systemen gehen über
      reine Automatisierung hinaus (z. B. in Form von vorprogrammierten Robotern in einer Fließbandproduktion).
      <br />
      <br />

      Hinweis: Orientieren Sie sich im Zweifel an dem Grad der Autonomie und Anpassungsfähigkeit des zu beurteilenden
      Systems und unterstellen Sie eher eine weite Auslegung des Begriffs „KI-System“.
    </fps-text>
  `;

  shouldContinue(answers: AICheckerAnswer[]): boolean {
    if (answers.includes(AICheckerAnswer.question_1_1)) {
      return true;
    }
    return false;
  }

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return false;
  }
}

export class Question2 implements BaseQuestion {
  question: string =
    'Verwenden Sie ein KI-System als Nutzer (z.B. „Ich möchte Chat-GPT in meinem Unternehmen nutzen“) oder als Anbieter / Software-Hersteller (z.B. „Ich möchte KI in meine Software einbauen“)?';
  type: QuestionType = 'single';
  choices: AICheckerAnswer[] = [AICheckerAnswer.question_2_1, AICheckerAnswer.question_2_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.question_2_1:
        return 'Nutzer';
      case AICheckerAnswer.question_2_2:
        return 'Anbieter / Software-Hersteller';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => null;

  shouldContinue(answers: AICheckerAnswer[]): boolean {
    if (answers.includes(AICheckerAnswer.question_2_1) || answers.includes(AICheckerAnswer.question_2_2)) {
      return true;
    }
    return false;
  }

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question1().isSkipped(answers) || !answers.includes(AICheckerAnswer.question_1_1);
  }
}

export class Question3 implements BaseQuestion {
  question: string = 'Bieten Sie ein KI-System in der EU an oder nutzen es in der EU?';
  type: QuestionType = 'single';
  choices: AICheckerAnswer[] = [AICheckerAnswer.question_3_1, AICheckerAnswer.question_3_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.question_3_1:
        return 'Ja';
      case AICheckerAnswer.question_3_2:
        return 'Nein';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => null;

  shouldContinue: (choices: AICheckerAnswer[]) => boolean = (answers: AICheckerAnswer[]) => {
    if (answers.includes(AICheckerAnswer.question_3_1)) {
      return true;
    }
    return false;
  };

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question2().isSkipped(answers);
  }
}

export class Question4 implements BaseQuestion {
  question: string =
    'Soll das KI-System als Sicherheitskomponente eines Produkts im unten genannten Sinne verwendet werden oder stellt das KI-System ein solches Produkt dar?';
  type: QuestionType = 'single';
  choices: AICheckerAnswer[] = [AICheckerAnswer.question_4_1, AICheckerAnswer.question_4_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.question_4_1:
        return 'Ja';
      case AICheckerAnswer.question_4_2:
        return 'Nein';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => html`
    <fps-text>
      Produkte in diesem Sinne sind zum Beispiel: Fahrzeuge, Eisenbahnen, Boote, Flugzeuge, Spielzeug, Funkanlagen,
      Medizinprodukte (z.B.: KI-basierte Auswertung von Aufnahmen aus der Radiologie). <br />
      <br />
      Sicherheitskomponenten in diesem Sinne sind zum Beispiel: aktive Verkehrsbeobachtung in einem Fahrzeug, eine
      KI-Anwendung in der robotergestützten Chirurgie.
    </fps-text>
  `;

  shouldContinue(answers: AICheckerAnswer[]): boolean {
    if (answers.includes(AICheckerAnswer.question_4_1) && answers.includes(AICheckerAnswer.question_2_1)) return false;
    return true;
  }

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question3().isSkipped(answers) || !answers.includes(AICheckerAnswer.question_3_1);
  }
}

export class Question5 implements BaseQuestion {
  question = html` Bieten Sie ein KI-System in einem der folgenden Bereiche an? `;
  type: QuestionType = 'multiple';
  choices: AICheckerAnswer[] = [
    AICheckerAnswer.QUESTION_5_1,
    AICheckerAnswer.QUESTION_5_2,
    AICheckerAnswer.QUESTION_5_3,
    AICheckerAnswer.QUESTION_5_4,
    AICheckerAnswer.QUESTION_5_5,
    AICheckerAnswer.QUESTION_5_6,
    AICheckerAnswer.QUESTION_5_7,
  ];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.QUESTION_5_1:
        return 'Biometrische Identifizierung, wie Fingerabdruck- oder Gesichtserkennung.';
      case AICheckerAnswer.QUESTION_5_2:
        return 'Verwaltung und Betrieb kritischer Infrastrukturen, wie intelligente Stromnetze.';
      case AICheckerAnswer.QUESTION_5_3:
        return 'Gesundheit, wie die automatisierte Auswertung von Tests.';
      case AICheckerAnswer.QUESTION_5_4:
        return 'Bildung, wie die Bewertung von SchülerInnen und StudentInnen.';
      case AICheckerAnswer.QUESTION_5_5:
        return 'Personalmanagement, wie die Bewerberauswahl.';
      case AICheckerAnswer.QUESTION_5_6:
        return 'Automatisierte Bewertung von Personen, zum Beispiel Bonitätsprüfungen.';
      case AICheckerAnswer.QUESTION_5_7:
        return 'Nein.';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => null;

  shouldContinue(answers: AICheckerAnswer[]): boolean {
    if (answers.includes(AICheckerAnswer.QUESTION_5_7) && answers.includes(AICheckerAnswer.question_2_1)) return false;
    return true;
  }

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question4().isSkipped(answers) || answers.includes(AICheckerAnswer.question_4_1);
  }
}

export class Question6 implements BaseQuestion {
  question = html`
    Setzen Sie das KI-System ausschließlich für die folgenden Zwecke ein und sind Sie in der Lage zu dokumentieren, dass
    deshalb kein Risiko für natürliche Personen von dem KI-System ausgeht?
  `;
  type: QuestionType = 'multiple';
  choices: AICheckerAnswer[] = [
    AICheckerAnswer.QUESTION_6_1,
    AICheckerAnswer.QUESTION_6_2,
    AICheckerAnswer.QUESTION_6_3,
    AICheckerAnswer.QUESTION_6_4,
    AICheckerAnswer.QUESTION_6_5,
  ];

  getChoiceText = (choice?: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.QUESTION_6_1:
        return 'Das KI-System ist zur Ausführung begrenzter „Hilfstätigkeiten“ bestimmt, z. B. Umwandlung unstrukturierter Daten in strukturierte Daten, Ordnung eingehender Dokumente in Kategorien, Erkennung von Duplikaten in einer großen Anzahl von Anwendungen.';
      case AICheckerAnswer.QUESTION_6_2:
        return 'Das KI-System soll das Ergebnis einer zuvor ausgeführten menschlichen Tätigkeit verbessern, z. B. eine sprachliche Verbesserung bereits erstellter Dokumente.';
      case AICheckerAnswer.QUESTION_6_3:
        return 'Das KI-System soll Entscheidungsmuster oder Abweichungen von Entscheidungsmustern erkennen, ohne eine menschliche Überprüfung zu ersetzen oder zu beeinflussen, z. B. KI-Systeme zur Überprüfung von Benotungsmustern eines Lehrers, um mögliche Unstimmigkeiten oder Anomalien aufzuzeigen.';
      case AICheckerAnswer.QUESTION_6_4:
        return html`Das KI-System soll eine menschliche Entscheidung vorbereiten, die beispielsweise die biometrische
          Identifizierung, kritische Infrastrukturen, die Gesundheit, die Bewertung von Schülern oder Beschäftigten, die
          automatisierte Bewertung von Personen, (z. B. Bonitätsprüfungen) betrifft. <br />
          Vorbereitend in diesem Sinne sind Funktionen wie die Indexierung, Suche, Text- und Sprachverarbeitung oder
          Verknüpfung von Daten mit anderen Datenquellen.`;
      case AICheckerAnswer.QUESTION_6_5:
        return 'Nein. Keine der oben genannten Zwecke sind einschlägig.';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => null;

  shouldContinue(answers: AICheckerAnswer[]): boolean {
    if (answers.includes(AICheckerAnswer.question_2_1)) return false;
    return true;
  }

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question5().isSkipped(answers) || answers.includes(AICheckerAnswer.QUESTION_5_7);
  }
}

export class Question8 implements BaseQuestion {
  question: string = 'Handelt es sich bei dem KI-System um eine sog. general-purpose AI?';
  type: QuestionType = 'single';
  choices: AICheckerAnswer[] = [AICheckerAnswer.QUESTION_8_1, AICheckerAnswer.QUESTION_8_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.QUESTION_8_1:
        return 'Ja';
      case AICheckerAnswer.QUESTION_8_2:
        return 'Nein';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => {
    return html`
      <fps-text>
        Eine general-purpose AI wurde mit großen Datenmengen vortrainiert und ist in der Lage, eine Vielzahl
        menschlicher kognitiver Tätigkeiten auszuführen. <br />
        <br />
        Dies sind beispielsweise die synthetische Erzeugung von Audio-, Bild-, Video- oder Textinhalten. <br />
        <br />
        Der Vorteil einer general-purpose AI ist, dass sie ohne kosten- und zeitintensives Training für eine Vielzahl
        von Anwendungsszenarien genutzt werden kann („Es ist nur noch ein Feintuning erforderlich“).
      </fps-text>
    `;
  };
  shouldContinue = (answers: AICheckerAnswer[]) => {
    if (!answers.includes(AICheckerAnswer.QUESTION_8_2)) return true;
    return false;
  };

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question4().isSkipped(answers) || answers.includes(AICheckerAnswer.question_2_1);
  }
}

export class Question9 implements BaseQuestion {
  question: string = 'Handelt es sich bei dem KI-System um eine sog. general-purpose AI mit systemischem Risiko?';
  type: QuestionType = 'multiple';
  choices: AICheckerAnswer[] = [AICheckerAnswer.QUESTION_9_1, AICheckerAnswer.QUESTION_9_2];

  getChoiceText = (choice: AICheckerAnswer) => {
    switch (choice) {
      case AICheckerAnswer.QUESTION_9_1:
        return html`
          Ja. Ein systemisches Risiko ist anzunehmen, denn das Modell hat eine besonders hohe Rechenleistung. <br />
          <fps-subtext italic ${css({ display: 'block', marginLeft: '20px', marginTop: '5px' })}>
            Eine besonders hohe Rechenleistung ist anzunehmen, wenn der Rechenaufwand für die Erstellung des Modells
            mehr als 1025 FLOPS („Floating Point Operations“) also "Rechenoperationen mit Bruchzahlen" beträgt. Bei
            großen LLM-Modellen wie ChatGPT dürfte das der Fall sein.
          </fps-subtext>
        `;
      case AICheckerAnswer.QUESTION_9_2:
        return 'Nein.';
      default:
        throw new Error('Invalid choice');
    }
  };

  getExplanation = () => null;

  shouldContinue: (choices: AICheckerAnswer[]) => boolean = (answers: AICheckerAnswer[]) => {
    return false;
  };

  isSkipped(answers: AICheckerAnswer[]): boolean {
    return new Question8().isSkipped(answers) || !answers.includes(AICheckerAnswer.QUESTION_8_1);
  }
}
